import type { TextInputProps } from '@components/input/text-input'
import { TextInput } from '@components/input/text-input'
import { differenceInYears } from 'date-fns/differenceInYears'
import { isPast } from 'date-fns/isPast'
import { isValid } from 'date-fns/isValid'
import { parse } from 'date-fns/parse'
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react'
import { PatternFormat as NumberFormat } from 'react-number-format'

import styles from './date-input.module.scss'

export const DATE_INPUT_FORMAT = 'MM / dd / yyyy'

/**
 * Enhanced date input with auto-advancing cursor after MM and DD
 */
export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
    ({ size, onValueChange, ...restOfProps }, ref) => {
        const inputRef = useRef<HTMLInputElement>(null)

        // Expose the input ref to parent components
        useImperativeHandle(ref, () => inputRef.current!, [])

        const handleValueChange = useCallback(
            (values: any, sourceInfo: any) => {
                const { value } = values
                const { source } = sourceInfo

                // Only auto-advance on user input, not programmatic changes
                if (source === 'event') {
                    const input = inputRef.current
                    if (input) {
                        // Use setTimeout to ensure the formatting is complete before moving cursor
                        setTimeout(() => {
                            const cleanValue = value.replace(/\D/g, '') // Remove non-digits
                            const currentPosition = input.selectionStart || 0

                            // Auto-advance after MM (2 digits for month)
                            if (
                                cleanValue.length === 2 &&
                                currentPosition <= 5
                            ) {
                                input.setSelectionRange(5, 5) // Position after "MM / "
                            }
                            // Auto-advance after DD (4 digits total: MMDD)
                            else if (
                                cleanValue.length === 4 &&
                                currentPosition >= 5 &&
                                currentPosition <= 10
                            ) {
                                input.setSelectionRange(10, 10) // Position after "MM / DD / "
                            }
                        }, 0)
                    }
                }

                // Call the original onValueChange if provided
                if (onValueChange) {
                    onValueChange(values, sourceInfo)
                }
            },
            [onValueChange],
        )

        return (
            <NumberFormat
                {...restOfProps}
                size={size}
                type='text'
                getInputRef={inputRef}
                format='## / ## / ####'
                placeholder='MM / DD / YYYY'
                mask={['M', 'M', 'D', 'D', 'Y', 'Y', 'Y', 'Y']}
                customInput={TextInput}
                className={styles.input}
                autoComplete='off'
                allowEmptyFormatting={false}
                onValueChange={handleValueChange}
            />
        )
    },
)

export const fromDateInput = (date: string) => {
    return parse(date, DATE_INPUT_FORMAT, new Date())
}

export const validateDateInput = (date: string) => {
    return isValid(fromDateInput(date))
}

export const AGE_RANGES = {
    child: [0, 13], // Young enough that there's some restrictions, <13 (<16 for Google services)
    teen: [13, 18], // Old enough to do normal teen stuff in the app, <18
    adult: [18, Infinity], // Legally independent adult, 18+
} as const

export type AgeType = keyof typeof AGE_RANGES

export const validateAgeAtLeast = (date: string, value: AgeType | number) => {
    const age = differenceInYears(new Date(), fromDateInput(date))
    return age >= (typeof value === 'number' ? value : AGE_RANGES[value][0])
}

export const validateAgeAtMost = (date: string, value: AgeType | number) => {
    const age = differenceInYears(new Date(), fromDateInput(date))
    return age <= (typeof value === 'number' ? value : AGE_RANGES[value][1])
}

export const validateAgeIsExactly = (date: string, value: number) => {
    const age = differenceInYears(new Date(), fromDateInput(date))
    return age === value
}

export const isDateInputInThePast = (date: string) => {
    return isPast(fromDateInput(date))
}

export type DateInputProps = Omit<TextInputProps, 'type'> & {
    onValueChange?: (values: any, sourceInfo: any) => void
}
